import produce from "immer";
import { clamp } from "./statsHelper";
import { IBitAction, IBitActionExecutor, IEntity } from "../../types";

type IPayload = {
  sourceIndex: number;
  targetIndex: number;
  action: IBitAction;
};

const physicalAttack = (draft: IEntity[], payload: IPayload) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { attack: sourceAttack } = draft[sourceIndex].stats;
  const { hp, defense } = draft[targetIndex].stats;
  const { intensity = 1 } = action;
  hp.value -= Math.max(sourceAttack.value - defense.value, 0) * intensity;
  clamp(hp);
  return true;
};

const attack = () => {
  return physicalAttack;
};

/**
 * Magic
 */

const drainSourceMP = (draft: IEntity[], payload: IPayload): boolean => {
  const { sourceIndex, action } = payload;
  const { mp } = draft[sourceIndex].stats;
  const { cost = 1 } = action;
  if (mp.value - cost < 0) return false;
  mp.value -= cost;
  clamp(mp);
  return true;
};

const heal = (draft: IEntity[], payload: IPayload) => {
  if (!drainSourceMP(draft, payload)) return false;
  const { targetIndex, action } = payload;
  const { hp } = draft[targetIndex].stats;
  const { intensity = 1 } = action;
  hp.value += intensity;
  clamp(hp);
  return true;
};

const refresh = (draft: IEntity[], payload: IPayload) => {
  const { targetIndex } = payload;
  if (!drainSourceMP(draft, payload)) return false;
  draft[targetIndex].statuses = [];
  return true;
};

const statusMagicAttack = (
  draft: IEntity[],
  payload: { sourceIndex: number; targetIndex: number; action: IBitAction }
): boolean => {
  const { targetIndex, action } = payload;
  if (action.type !== "magic" || action.subtype !== "green") return false;
  if (!drainSourceMP(draft, payload)) return false;

  const { statuses } = draft[targetIndex];
  draft[targetIndex].statuses = statuses
    .filter(status => status.key !== action.key)
    .concat({ key: action.key, start: 0, end: 6 });

  return true;
};

const elementalMagicAttack = (draft: IEntity[], payload: IPayload) => {
  const { sourceIndex, targetIndex, action } = payload;
  const { mpAttack: sourceMpAttack } = draft[sourceIndex].stats;
  const { hp, mpDefense } = draft[targetIndex].stats;
  const { affinity, intensity = 1 } = action;
  if (!drainSourceMP(draft, payload)) return false;

  const affinityMultiplier =
    affinity !== undefined ? draft[targetIndex].affinities[affinity] : 1;
  hp.value -=
    Math.max(sourceMpAttack.value - mpDefense.value, 0) *
    intensity *
    affinityMultiplier;
  clamp(hp);

  return true;
};

// eslint-disable-next-line complexity
const magic = (action?: IBitAction) => {
  const failedAttack = () => false;
  if (!action) return failedAttack;
  if (action.type !== "magic") return failedAttack;

  if (action.key === "heal") return heal;
  if (action.key === "refresh") return refresh;

  if (action.subtype === "green") return statusMagicAttack;
  if (action.subtype === "black") return elementalMagicAttack;
  return failedAttack;
};

type IBitActionExecutorHOF = Record<
  IBitAction["type"],
  (action?: IBitAction) => IBitActionExecutor[IBitAction["key"]]
>;

export const actions: IBitActionExecutorHOF = { attack, magic };

export const act = (
  actors: IEntity[],
  sourceIndex: number,
  targetIndex: number,
  action: IBitAction
): IEntity[] => {
  return produce(actors, draft => {
    const executor = actions[action.type](action);
    const succeeded = executor(draft, { sourceIndex, targetIndex, action });
    draft[targetIndex].lastAction = { ...action, succeeded };
  });
};
